<template>
    <div class="gallery-box">
        <Transition name="fade" mode="out-in" :duration="150">
            <div class="wrapper" @click.stop="dismiss" v-if="visible" :class="{ zoomed: zoomed }">
                <button class="close-btn" @click.stop="dismiss">
                    <FeatherIcon name="x" />
                </button>

                <div class="stage">
                    <div
                        v-for="(img, index) in images"
                        :key="img"
                        class="gallery-image"
                        :class="{ current: index === current }"
                        :style="styleFor(index)"
                        @click.stop="onImageClick(index)"
                    >
                        <div class="image-actions" v-if="index === current && !zoomed && !adding && onRemove">
                            <button class="remove" @click.stop="handleRemove" v-if="onRemove">
                                <FeatherIcon name="trash-2" />
                            </button>
                        </div>
                        <img :src="img" alt="" :ref="(el) => setRef(el, index)" @load="onLoad(index)" :class="cursorClass(index)" />
                    </div>
                </div>

                <Transition name="fade">
                    <div class="add-card" v-if="onAdd && !zoomed && !adding && images.length > 0" @click.stop="openZone">
                        <FeatherIcon name="plus" />
                        <span>Ajouter un ticket</span>
                    </div>
                </Transition>

                <div class="add-panel" v-if="onAdd && !zoomed && adding" @click.stop>
                    <label
                        class="add-zone"
                        :class="{ over: over }"
                        @click.stop
                        @drop.prevent.stop="onDrop"
                        @dragover.prevent="over = true"
                        @dragleave.prevent="over = false"
                    >
                        <span v-if="over">Déposez votre fichier ici</span>
                        <span v-else>Cliquez ici pour sélectionner vos fichiers ou glissez-les dans cette zone. Vous pouvez ajouter plusieurs fichiers à la fois.</span>
                        <input ref="fileInput" type="file" multiple :accept="accept" @change="onChange" />
                    </label>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script lang="ts" setup>
import { ref, Ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useGalleryBox } from '.'
import { accept, convertUploadFile } from '../file-upload-box/convertFile'
import { useNProgress } from '@vueuse/integrations/useNProgress'

const zoomed: Ref<boolean> = ref(false)
const canZoom: Ref<boolean> = ref(false)
const baseWidth: Ref<number> = ref(0)
const currentImage = ref(null)

const adding: Ref<boolean> = ref(false)
const over: Ref<boolean> = ref(false)
const processing: Ref<boolean> = ref(false)
const fileInput = ref<HTMLInputElement>(null)

const THUMB_SCALE = 0.16
const THUMB_LEFT = 80
const THUMB_STEP = 60
const THUMB_TOP = 100
const THUMB_FAN_ANGLE = 6
const THUMB_TILT_MAX = 18
const THUMB_ARC = 7

const { start, done } = useNProgress()

const { galleryBox } = useGalleryBox()
const { images, visible, dismiss, current, onRemove, onAdd, startAdding } = galleryBox

const setRef = (el: any, index: number) => {
    if (index === current.value) currentImage.value = el
}

const updateMeasure = () => {
    const el = currentImage.value
    if (!el) return
    baseWidth.value = el.clientWidth || baseWidth.value
    canZoom.value = !!el.naturalHeight && el.naturalHeight > el.clientHeight
}

const onLoad = (index: number) => {
    if (index === current.value) updateMeasure()
}

const styleFor = (index: number) => {
    const o = index - current.value
    if (zoomed.value) {
        return index === current.value ? {} : { display: 'none' }
    }
    if (adding.value) {
        const centerOffset = index - (images.value.length - 1) / 2
        const dx = -window.innerWidth / 2 + THUMB_LEFT + index * THUMB_STEP
        const dy = -window.innerHeight / 2 + THUMB_TOP + centerOffset * centerOffset * THUMB_ARC
        const tilt = Math.max(-THUMB_TILT_MAX, Math.min(THUMB_TILT_MAX, centerOffset * THUMB_FAN_ANGLE))
        return {
            transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(${THUMB_SCALE}) rotate(${tilt}deg)`,
            zIndex: index,
            filter: 'brightness(1)'
        }
    }
    const abs = Math.abs(o)
    if (abs > 2) {
        return { opacity: 0, pointerEvents: 'none', transform: 'translate(-50%, -50%) scale(0.3)', zIndex: 0 }
    }
    const w = baseWidth.value || window.innerWidth * 0.4
    const sign = Math.sign(o)
    let shift = 0
    let scale = 1
    let z = 30
    let brightness = 1
    if (abs === 1) {
        shift = 0.58 * w
        scale = 0.55
        z = 20
        brightness = 0.65
    } else if (abs === 2) {
        shift = 0.92 * w
        scale = 0.4
        z = 10
        brightness = 0.45
    }
    return {
        transform: `translate(-50%, -50%) translateX(${sign * shift}px) scale(${scale})`,
        zIndex: z,
        filter: `brightness(${brightness})`
    }
}

const cursorClass = (index: number) => {
    if (adding.value) return 'is-neighbor'
    if (index !== current.value) return 'is-neighbor'
    if (zoomed.value) return 'is-zoom-out'
    return canZoom.value ? 'is-zoom-in' : ''
}

const onImageClick = (index: number) => {
    if (adding.value) {
        current.value = index
        closeZone()
        return
    }
    if (index !== current.value) {
        current.value = index
        return
    }
    zoom()
}

const zoom = () => {
    if (!currentImage.value || !currentImage.value.naturalHeight) return

    if (!zoomed.value && currentImage.value.naturalHeight > currentImage.value.height) {
        zoomed.value = !zoomed.value
        return
    }
    if (zoomed.value) {
        zoomed.value = false
        return
    }
}

const next = (value: number) => {
    const n = current.value + value
    if (n < 0 || n > images.value.length - 1) return
    current.value = n
}

const openZone = () => {
    if (processing.value) return
    adding.value = true
}

const closeZone = () => {
    adding.value = false
    over.value = false
    if (fileInput.value) fileInput.value.value = ''
    if (images.value.length === 0) dismiss()
}

const processFiles = async (files: FileList) => {
    if (!onAdd.value || processing.value) return
    if (!files || !files.length) return

    processing.value = true
    start()

    const collected: string[] = []
    for (const file of Array.from(files)) {
        await convertUploadFile(file, async (b64, filename) => {
            const added = await onAdd.value(b64, filename)
            if (!added) return
            const urls = (Array.isArray(added) ? added : [added]).filter(Boolean)
            collected.push(...urls)
        })
    }

    done()
    processing.value = false

    if (collected.length) {
        const firstNewIndex = images.value.length
        images.value.push(...collected)
        current.value = firstNewIndex
    }

    closeZone()
}

const onChange = async (ev: any) => {
    await processFiles(ev.target.files)
}

const onDrop = async (ev: any) => {
    over.value = false
    await processFiles(ev.dataTransfer.files)
}

const handleRemove = async () => {
    if (!onRemove.value) return
    const indexToRemove = current.value
    const confirmed = await onRemove.value(indexToRemove, images.value[indexToRemove])
    if (confirmed === false) return
    images.value.splice(indexToRemove, 1)
    if (images.value.length === 0) {
        dismiss()
        return
    }
    if (current.value >= images.value.length) {
        current.value = images.value.length - 1
    }
}

const handleKeydown = (e: KeyboardEvent) => {
    if (!visible.value) return
    if (e.key === 'Escape') {
        if (adding.value) closeZone()
        return
    }
    if (images.value.length <= 1) return
    if (e.key === 'ArrowRight') {
        next(1)
    } else if (e.key === 'ArrowLeft') {
        next(-1)
    }
}

onMounted(() => {
    window.addEventListener('keydown', handleKeydown)
    window.addEventListener('resize', updateMeasure)
})

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown)
    window.removeEventListener('resize', updateMeasure)
})

watch(current, () => {
    zoomed.value = false
    nextTick(updateMeasure)
})

watch(images, () => {
    zoomed.value = false
    nextTick(updateMeasure)
})

watch(visible, (value) => {
    if (value) {
        if (startAdding.value) adding.value = true
        return
    }
    adding.value = false
    over.value = false
    processing.value = false
    if (fileInput.value) fileInput.value.value = ''
})
</script>

<style lang="scss" scoped>
.gallery-box {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1000;
}

.wrapper {
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.45);
    overflow: scroll;
    padding: 10px;

    button.close-btn {
        position: absolute;
        top: 20px;
        right: 20px;
        border: 0;
        background: transparent;
        font-size: 2rem;
        color: #fff;
        width: 60px;
        height: 60px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border-radius: 50%;
        box-shadow: none;
        z-index: 100;
        transition: transform 0.2s ease;

        &:active {
            transform: scale(0.96);
        }
    }

    .stage {
        position: relative;
        width: 100%;
        height: 100%;
    }

    .gallery-image {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        transition:
            transform 0.3s ease,
            filter 0.3s ease,
            opacity 0.3s ease;

        img {
            display: block;
            max-height: 90vh;
            max-width: 90vw;
            box-shadow: 0 0 30px rgba(0, 0, 0, 0.45);

            &.is-neighbor {
                cursor: pointer;
            }

            &.is-zoom-in {
                cursor: zoom-in;
            }

            &.is-zoom-out {
                cursor: zoom-out;
            }
        }

        .image-actions {
            position: absolute;
            top: 15px;
            right: 15px;
            display: flex;
            gap: 10px;
            z-index: 10;

            button {
                border: 0;
                width: 54px;
                height: 54px;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                border-radius: 50%;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                background: rgba(255, 255, 255, 0.9);
                font-size: 1.5rem;
                transition:
                    background 0.2s ease,
                    color 0.2s ease,
                    transform 0.2s ease;

                &:hover {
                    color: #fff;
                    transform: scale(1.08);
                }

                &:active {
                    transform: scale(0.96);
                }
            }

            .remove {
                color: var(--c-color-alert);

                &:hover {
                    background: var(--c-color-alert);
                }
            }
        }
    }

    .add-card {
        position: absolute;
        top: 50%;
        right: 50px;
        transform: translateY(-50%);
        z-index: 40;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        width: 150px;
        height: 200px;
        border-radius: 12px;
        border: 2px dashed rgba(255, 255, 255, 0.55);
        background: transparent;
        color: #fff;
        text-align: center;
        font-weight: 600;
        cursor: pointer;
        transition:
            background 0.2s ease,
            border-color 0.2s ease,
            transform 0.2s ease;

        .feather {
            font-size: 2rem;
        }

        span {
            font-weight: 600;
        }

        &:hover {
            background: var(--c-color-primary);
            border-color: var(--c-color-primary);
            transform: translateY(-50%) scale(1.04);
        }
    }

    .add-panel {
        position: absolute;
        inset: 0;
        z-index: 90;
        display: flex;
        justify-content: center;
        align-items: center;
        pointer-events: none;

        .add-zone {
            pointer-events: auto;
        }
    }

    .add-zone {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        width: min(720px, 75vw);
        height: min(440px, 60vh);
        padding: 20px;
        border-radius: 12px;
        border: 2px dashed var(--c-color-border-hard);
        background: transparent;
        color: #fff;
        text-align: center;
        font-size: 1.1rem;
        font-weight: 600;
        cursor: pointer;
        overflow: hidden;
        transition:
            background 0.2s ease,
            color 0.2s ease,
            border-color 0.2s ease;

        span {
            max-width: 400px;
        }

        input {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            opacity: 0;
            cursor: pointer;
        }

        &.over {
            background: var(--c-color-primary);
            border-color: var(--c-color-primary);
            color: #fff;
        }
    }

    &.zoomed {
        padding: 0px 10px;

        .stage {
            height: auto;
        }

        .gallery-image {
            position: static;
            transform: none;

            img {
                max-height: none;
                max-width: 100%;
                margin: 0 auto;
            }
        }
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
